import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ClosingCta from "@/components/sections/ClosingCta";

const BUILDS = [
  "Instant reply and qualification for website, ad and WhatsApp enquiries",
  "Follow-up sequences that run until a lead books or opts out",
  "Lead scoring and routing to the right salesperson",
  "CRM automation: deals created, updated and reminded without manual entry",
  "Booking, reminders and no-show recovery",
  "Reactivation of old leads and past customers",
  "Weekly pipeline reports, sent automatically",
];

const STEPS = [
  { title: "Discovery call", body: "Your goals, workflows, markets, tools and team." },
  { title: "Proposal", body: "Each workflow with its trigger, steps and result, plus timeline and price." },
  { title: "Build, test and hand over", body: "We build it, test it with your team and hand it over in your accounts." },
];

export default function CustomWorkflowsPage() {
  return (
    <main data-testid="custom-workflows-page">
      <Seo
        title="Custom AI Sales Workflows"
        description="Custom AI sales workflows in Singapore for enquiry handling, follow-up, booking, CRM automation and reporting, built around how you sell."
        path="/custom-workflows"
      />
      <PageHero
        label="Custom AI Sales Workflows"
        headline="Sales workflows built around how you sell."
        subline="When outbound is not the only gap, we map your whole sales process and automate the parts that slow you down."
      />

      <section className="px-6 sm:px-8 pb-20 sm:pb-24" data-testid="what-we-can-build">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C]">
            What we can build
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-4">
            {BUILDS.map((b, i) => (
              <li
                key={b}
                className="flex gap-3 bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-5"
                data-testid={`build-item-${i + 1}`}
              >
                <Check className="w-5 h-5 text-[#C8703A] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span className="text-sm text-[#1F1F1C] leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[#3D472D] px-6 sm:px-8 py-20 sm:py-24" data-testid="custom-how-it-works">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#F4E9D8]">How it works</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div
                key={s.title}
                className="rounded-2xl border border-[#F4E9D8]/20 bg-[#55633F]/50 p-7"
                data-testid={`custom-step-${i + 1}`}
              >
                <span className="font-fraunces font-extrabold text-sm text-[#C8703A]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-fraunces font-extrabold text-base md:text-lg text-[#F4E9D8]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#F4E9D8]/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/audit?enquiry=custom"
              className="btn-press inline-flex items-center gap-2 rounded-2xl bg-[#C8703A] text-[#F4E9D8] px-7 py-4 font-semibold text-sm sm:text-base hover:bg-[#B35F2C]"
              data-testid="custom-discovery-cta"
            >
              Book a discovery call <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>

      <ClosingCta testId="custom-closing-cta" />
    </main>
  );
}
